import React, { useState, useRef, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  Popper,
  Paper,
  ClickAwayListener,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  TableContainer,
} from '@mui/material';
import {
  Close as CloseIcon,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatStrikethrough,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  FormatIndentDecrease,
  FormatIndentIncrease,
  FormatListBulleted,
  FormatListNumbered,
  Link as LinkIcon,
  FormatClear,
  Image as ImageIcon,
  Undo,
  Redo,
  FormatColorText,
  FormatColorFill,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import AddSignatureAndSalutationModal, { SignatureAndSalutationData } from './AddSignatureAndSalutationModal';
import { deleteSignatureSalutation, getSignatureSalutations, SignatureSalutationItem, updateSignatureSalutation, UpdateSignatureSalutationRequest } from '../services/emailService';
import { useToast } from '../contexts/ToastContext';
import { STORAGE_KEYS } from '../helpers/constants';
import LinkifyText from '../helpers/customLinkify';
import { useChannelAdmin } from '../hooks/useChannelAdmin';

interface SignatureAndSalutationProps {
  onNext?: () => void;
  onSkip?: () => void;
  onSignatureChange?: (signature: string) => void;
  onSalutationChange?: (salutation: string) => void;
  initialSignature?: string;
  initialSalutation?: string;
  channelId: string;
  // New props for signature management
  showManagement?: boolean; // Whether to show the management interface
  signatureData?: SignatureAndSalutationData[];
  onSignatureDataChange?: (data: SignatureAndSalutationData[]) => void;
  hideAddButton?: boolean; // Whether to hide the internal Add button
  refreshTrigger?: number; // Trigger to refresh data when this value changes
}

const Signature: React.FC<{
  value: string;
  onChange: (value: string) => void;
  onEditSignature: () => void;
}> = ({ value, onChange, onEditSignature }) => {
  const [fontFamily, setFontFamily] = useState('Arial');
  const [fontSize, setFontSize] = useState('3');
  const [alignment, setAlignment] = useState('left');

  const editorRef = useRef<HTMLDivElement>(null);

  // Effect to update contentEditable's innerHTML when the value prop changes
  // while preserving cursor position.
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      const selection = window.getSelection();
      let range: Range | null = null;

      // Save current cursor position if it's within the editor
      if (selection && selection.rangeCount > 0) {
        const currentRange = selection.getRangeAt(0);
        if (editorRef.current.contains(currentRange.commonAncestorContainer)) {
          range = currentRange;
        }
      }

      editorRef.current.innerHTML = value ? DOMPurify.sanitize(value) : '';

      // Restore cursor position
      if (range && selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }, [value]);

  const handleFormat = (command: string, commandValue?: string) => {
    document.execCommand(command, false, commandValue);
    editorRef.current?.focus(); // Re-focus after command
  };

  const handleFontChange = (event: SelectChangeEvent<string>) => {
    const newFont = event.target.value;
    setFontFamily(newFont);
    handleFormat('fontName', newFont);
  };

  const handleFontSizeChange = (event: SelectChangeEvent<string>) => {
    const newSize = event.target.value;
    setFontSize(newSize);
    handleFormat('fontSize', newSize);
  };

  const handleAlignment = (newAlignment: string) => {
    setAlignment(newAlignment);
    handleFormat(`justify${newAlignment.charAt(0).toUpperCase() + newAlignment.slice(1)}`);
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
          Signature *
        </Typography>
        {/* <Link
          component="button"
          variant="body2"
          onClick={onEditSignature}
          sx={{ textDecoration: 'none' }}
        >
          Edit Signature
        </Link> */}
      </Box>
      <Box
        sx={{
          border: '1px solid #ccc',
          borderRadius: 1,
          overflow: 'hidden',
          '&:focus-within': {
            borderColor: '#0078d4',
            boxShadow: '0 0 0 1px #0078d4',
          },
        }}
      >
        {/* Toolbar */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            p: 1,
            borderBottom: '1px solid #eee',
            backgroundColor: '#f9f9f9',
          }}
        >
          {/* Undo/Redo */}
          <Tooltip title="Undo">
            <IconButton size="small" onClick={() => handleFormat('undo')}>
              <Undo fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Redo">
            <IconButton size="small" onClick={() => handleFormat('redo')}>
              <Redo fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Text Formatting */}
          <Tooltip title="Bold">
            <IconButton size="small" onClick={() => handleFormat('bold')}>
              <FormatBold fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Italic">
            <IconButton size="small" onClick={() => handleFormat('italic')}>
              <FormatItalic fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Underlined">
            <IconButton size="small" onClick={() => handleFormat('underline')}>
              <FormatUnderlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Strikethrough">
            <IconButton size="small" onClick={() => handleFormat('strikeThrough')}>
              <FormatStrikethrough fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Alignment */}
          <Tooltip title="Align Left">
            <IconButton size="small" onClick={() => handleAlignment('left')}>
              <FormatAlignLeft fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Align Center">
            <IconButton size="small" onClick={() => handleAlignment('center')}>
              <FormatAlignCenter fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Align Right">
            <IconButton size="small" onClick={() => handleAlignment('right')}>
              <FormatAlignRight fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Justify">
            <IconButton size="small" onClick={() => handleAlignment('full')}>
              <FormatAlignJustify fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Indentation */}
          <Tooltip title="Decrease Indent">
            <IconButton size="small" onClick={() => handleFormat('outdent')}>
              <FormatIndentDecrease fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Increase Indent">
            <IconButton size="small" onClick={() => handleFormat('indent')}>
              <FormatIndentIncrease fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Lists */}
          <Tooltip title="Bullet List">
            <IconButton size="small" onClick={() => handleFormat('insertUnorderedList')}>
              <FormatListBulleted fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Numbered List">
            <IconButton size="small" onClick={() => handleFormat('insertOrderedList')}>
              <FormatListNumbered fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Font Family */}
          <FormControl variant="standard" sx={{ minWidth: 120 }}>
            <Select
              value={fontFamily}
              onChange={handleFontChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Font family' }}
              sx={{ '.MuiSelect-select': { py: 0.5, px: 1 } }}
            >
              <MenuItem value="Arial">Arial</MenuItem>
              <MenuItem value="Times New Roman">Times New Roman</MenuItem>
              <MenuItem value="Verdana">Verdana</MenuItem>
            </Select>
          </FormControl>

          {/* Font Size */}
          <FormControl variant="standard" sx={{ minWidth: 60 }}>
            <Select
              value={fontSize}
              onChange={handleFontSizeChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Font size' }}
              sx={{ '.MuiSelect-select': { py: 0.5, px: 1 } }}
            >
              {[1, 2, 3, 4, 5, 6, 7].map((size) => (
                <MenuItem key={size} value={size.toString()}>{size}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Link, Image, Clear */}
          <Tooltip title="Insert Link">
            <IconButton size="small" onClick={() => handleFormat('createLink')}>
              <LinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Insert Image">
            <IconButton size="small" onClick={() => handleFormat('insertImage')}>
              <ImageIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Clear Formatting">
            <IconButton size="small" onClick={() => handleFormat('removeFormat')}>
              <FormatClear fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Editor Area */}
        <Box
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning={true}
          onInput={(e) => {
            const newValue = e.currentTarget.innerHTML;
            onChange(newValue);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault(); // Prevent default behavior (e.g., new line in some contexts)
              document.execCommand('insertHTML', false, '<br>'); // Insert a line break
            }
          }}
          sx={{
            minHeight: 96, // 4 rows * 24px (equivalent to TextField rows={4})
            p: 1.5,
            outline: 'none',
            fontFamily: 'Arial, sans-serif',
            fontSize: '14px',
            lineHeight: 1.5,
            '&:empty:before': {
              content: 'attr(data-placeholder)',
              color: '#999',
              fontStyle: 'italic',
            },
            '&:focus': {
              // Focus styles are handled by the parent Box via &:focus-within
            },
          }}
          data-placeholder="Your signature will appear here..."
        />
      </Box>
    </Box>
  );
};

const Salutation: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const [fontFamily, setFontFamily] = useState('Arial');
  const [fontSize, setFontSize] = useState('3');
  const [alignment, setAlignment] = useState('left');

  const [colorAnchor, setColorAnchor] = useState<null | HTMLElement>(null);
  const [colorMode, setColorMode] = useState<'text' | 'background' | null>(null);

  const editorRef = useRef<HTMLDivElement>(null);
  const savedRangeRef = useRef<Range | null>(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      const selection = window.getSelection();
      let range: Range | null = null;
      if (selection && selection.rangeCount > 0) {
        const currentRange = selection.getRangeAt(0);
        if (editorRef.current.contains(currentRange.commonAncestorContainer)) {
          range = currentRange;
        }
      }
      editorRef.current.innerHTML = value ? DOMPurify.sanitize(value) : '';
      if (range && selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }, [value]);

  useEffect(() => {
    try { document.execCommand('styleWithCSS', false, 'true'); } catch {}
  }, []);

  const handleFormat = (command: string, commandValue?: string) => {
    document.execCommand(command, false, commandValue);
    editorRef.current?.focus();
  };

  const handleFontChange = (event: SelectChangeEvent<string>) => {
    const newFont = event.target.value;
    setFontFamily(newFont);
    handleFormat('fontName', newFont);
  };

  const handleFontSizeChange = (event: SelectChangeEvent<string>) => {
    const newSize = event.target.value;
    setFontSize(newSize);
    handleFormat('fontSize', newSize);
  };

  const handleAlignment = (newAlignment: string) => {
    setAlignment(newAlignment);
    handleFormat(`justify${newAlignment.charAt(0).toUpperCase() + newAlignment.slice(1)}`);
  };

  const getSelectionRangeWithinEditor = (): Range | null => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;
    const range = selection.getRangeAt(0);
    if (!editorRef.current) return null;
    return editorRef.current.contains(range.commonAncestorContainer) ? range : null;
  };
  const saveSelectionRange = () => { savedRangeRef.current = getSelectionRangeWithinEditor(); };
  const restoreSavedSelection = () => {
    const selection = window.getSelection();
    if (selection && savedRangeRef.current) {
      selection.removeAllRanges();
      selection.addRange(savedRangeRef.current);
    }
  };

  const wrapSelectionWithSpan = (style: Partial<CSSStyleDeclaration>) => {
    const range = getSelectionRangeWithinEditor();
    if (!range || range.collapsed) return;
    const span = document.createElement('span');
    Object.assign(span.style, style);
    range.surroundContents(span);
    editorRef.current?.focus();
  };
  const applyTextColor = (color: string) => {
    restoreSavedSelection();
    const range = getSelectionRangeWithinEditor();
    if (!range || range.collapsed) return;
    const applied = document.execCommand('foreColor', false, color);
    if (!applied) wrapSelectionWithSpan({ color });
    editorRef.current && onChange(editorRef.current.innerHTML);
  };
  const applyBackgroundColor = (color: string) => {
    restoreSavedSelection();
    const range = getSelectionRangeWithinEditor();
    if (!range || range.collapsed) return;
    const appliedHilite = document.execCommand('hiliteColor', false, color);
    const appliedBack = appliedHilite ? true : document.execCommand('backColor', false, color);
    if (!appliedBack) wrapSelectionWithSpan({ backgroundColor: color });
    editorRef.current && onChange(editorRef.current.innerHTML);
  };

  const openColor = Boolean(colorAnchor);
  const handleOpenTextColor = (e: React.MouseEvent<HTMLElement>) => { saveSelectionRange(); setColorMode('text'); setColorAnchor(e.currentTarget); };
  const handleOpenBackgroundColor = (e: React.MouseEvent<HTMLElement>) => { saveSelectionRange(); setColorMode('background'); setColorAnchor(e.currentTarget); };
  const handleCloseColor = () => { setColorAnchor(null); setColorMode(null); };

  const swatch = (color: string, onPick: (c: string) => void) => (
    <Box key={color} onClick={() => { onPick(color); handleCloseColor(); }} sx={{ width: 18, height: 18, bgcolor: color, borderRadius: 0.5, cursor: 'pointer', border: '1px solid rgba(0,0,0,0.15)' }} />
  );
  const baseRow = ['#000000','#434343','#666666','#999999','#b7b7b7','#cccccc','#d9d9d9','#efefef'];
  const vividRow = ['#ff0000','#ff9900','#ffff00','#00ff00','#00ffff','#4a86e8','#0000ff','#9900ff'];
  const rows = [
    ['#ffffff','#f4cccc','#fce5cd','#fff2cc','#d9ead3','#d0e0e3','#cfe2f3','#d9d2e9'],
    ['#ead1dc','#ea9999','#f9cb9c','#ffe599','#b6d7a8','#a2c4c9','#9fc5e8','#b4a7d6'],
    ['#d5a6bd','#e06666','#f6b26b','#ffd966','#93c47d','#76a5af','#6fa8dc','#8e7cc3'],
    ['#c27ba0','#cc0000','#e69138','#f1c232','#6aa84f','#45818e','#3d85c6','#674ea7'],
    ['#a64d79','#990000','#b45f06','#bf9000','#38761d','#134f5c','#0b5394','#351c75'],
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}>
        Salutation
      </Typography>
      <Box sx={{ border: '1px solid #ccc', borderRadius: 1, overflow: 'hidden', '&:focus-within': { borderColor: '#0078d4', boxShadow: '0 0 0 1px #0078d4' } }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, p: 1, borderBottom: '1px solid #eee', backgroundColor: '#f9f9f9' }}>
          <Tooltip title="Undo"><IconButton size="small" onClick={() => handleFormat('undo')}><Undo fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Redo"><IconButton size="small" onClick={() => handleFormat('redo')}><Redo fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Bold"><IconButton size="small" onClick={() => handleFormat('bold')}><FormatBold fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Italic"><IconButton size="small" onClick={() => handleFormat('italic')}><FormatItalic fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Underlined"><IconButton size="small" onClick={() => handleFormat('underline')}><FormatUnderlined fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Strikethrough"><IconButton size="small" onClick={() => handleFormat('strikeThrough')}><FormatStrikethrough fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Align Left"><IconButton size="small" onClick={() => handleAlignment('left')}><FormatAlignLeft fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Align Center"><IconButton size="small" onClick={() => handleAlignment('center')}><FormatAlignCenter fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Align Right"><IconButton size="small" onClick={() => handleAlignment('right')}><FormatAlignRight fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Justify"><IconButton size="small" onClick={() => handleAlignment('full')}><FormatAlignJustify fontSize="small" /></IconButton></Tooltip>
          <FormControl variant="standard" sx={{ minWidth: 120 }}>
            <Select value={fontFamily} onChange={handleFontChange} displayEmpty sx={{ '.MuiSelect-select': { py: 0.5, px: 1 } }}>
              <MenuItem value="Arial">Arial</MenuItem>
              <MenuItem value="Times New Roman">Times New Roman</MenuItem>
              <MenuItem value="Verdana">Verdana</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="standard" sx={{ minWidth: 60 }}>
            <Select value={fontSize} onChange={handleFontSizeChange} displayEmpty sx={{ '.MuiSelect-select': { py: 0.5, px: 1 } }}>
              {[1,2,3,4,5,6,7].map((s) => (<MenuItem key={s} value={s.toString()}>{s}</MenuItem>))}
            </Select>
          </FormControl>
          <Tooltip title="Text colour"><IconButton size="small" onClick={handleOpenTextColor}><FormatColorText fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Background colour"><IconButton size="small" onClick={handleOpenBackgroundColor}><FormatColorFill fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Insert Link"><IconButton size="small" onClick={() => handleFormat('createLink')}><LinkIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Insert Image"><IconButton size="small" onClick={() => handleFormat('insertImage')}><ImageIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Clear Formatting"><IconButton size="small" onClick={() => handleFormat('removeFormat')}><FormatClear fontSize="small" /></IconButton></Tooltip>
        </Box>

        <Popper open={openColor} anchorEl={colorAnchor} placement="bottom-start" style={{ zIndex: 2200 }}>
          <ClickAwayListener onClickAway={handleCloseColor}>
            <Paper elevation={3} sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 3 }}>
                {(!colorMode || colorMode === 'background') && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <FormatColorFill sx={{ fontSize: 16 }} />
                    <Typography variant="caption">Background colour</Typography>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 18px)', gap: 0.5, mb: 1 }}>
                    {baseRow.map((c) => swatch(c, (color) => applyBackgroundColor(color)))}
                  </Box>
                  {[...rows, vividRow].map((r, idx) => (
                    <Box key={idx} sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 18px)', gap: 0.5, mb: 0.5 }}>
                      {r.map((c) => swatch(c, (color) => applyBackgroundColor(color)))}
                    </Box>
                  ))}
                </Box>
                )}
                {(!colorMode || colorMode === 'text') && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <FormatColorText sx={{ fontSize: 16 }} />
                    <Typography variant="caption">Text colour</Typography>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 18px)', gap: 0.5, mb: 1 }}>
                    {baseRow.map((c) => swatch(c, (color) => applyTextColor(color)))}
                  </Box>
                  {[...rows, vividRow].map((r, idx) => (
                    <Box key={idx} sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 18px)', gap: 0.5, mb: 0.5 }}>
                      {r.map((c) => swatch(c, (color) => applyTextColor(color)))}
                    </Box>
                  ))}
                </Box>
                )}
              </Box>
            </Paper>
          </ClickAwayListener>
        </Popper>

        <Box
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning={true}
          onInput={(e) => onChange(e.currentTarget.innerHTML)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); document.execCommand('insertHTML', false, '<br>'); } }}
          sx={{ minHeight: 96, p: 1.5, outline: 'none', fontFamily: 'Arial, sans-serif', fontSize: '14px', lineHeight: 1.5, '&:empty:before': { content: 'attr(data-placeholder)', color: '#999', fontStyle: 'italic' } }}
          data-placeholder="Enter your salutation..."
        />
      </Box>
    </Box>
  );
};

const AddSignatureModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (signature: string) => void;
  initialSignature: string;
}> = ({ open, onClose, onSave, initialSignature }) => {
  const [tempSignature, setTempSignature] = useState(initialSignature);
  const [isDefault, setIsDefault] = useState(false);

  const handleSave = () => {
    onSave(tempSignature);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Add Signature</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          multiline
          rows={6}
          fullWidth
          placeholder="Start typing your signature here..."
          value={tempSignature}
          onChange={(e) => setTempSignature(e.target.value)}
          sx={{ mb: 2 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              name="defaultSignature"
              color="primary"
            />
          }
          label="Default"
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SignatureAndSalutation: React.FC<SignatureAndSalutationProps> = ({
  onNext,
  onSkip,
  onSignatureChange,
  onSalutationChange,
  initialSignature = '',
  initialSalutation = '',
  showManagement = false,
  signatureData = [],
  onSignatureDataChange,
  channelId,
  hideAddButton = false,
  refreshTrigger = 0,
}) => {
  const muiTheme = useTheme();
  const [signature, setSignature] = useState(initialSignature);
  const [salutation, setSalutation] = useState(initialSalutation);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [localSignatureData, setLocalSignatureData] = useState<SignatureAndSalutationData[]>(signatureData);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    open: boolean;
    index: number;
    signatureName: string;
    id?: string;
  }>({
    open: false,
    index: -1,
    signatureName: '',
  });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSignature, setEditingSignature] = useState<SignatureSalutationItem | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const { showSuccess, showError } = useToast();
  
  // Check if current user is channel admin (only when showManagement is true)
  const { isChannelAdmin } = useChannelAdmin(showManagement ? channelId : undefined);

  // Pagination state
  const [apiSignatureData, setApiSignatureData] = useState<SignatureSalutationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false); // Start with false to prevent premature message
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [initialLoad, setInitialLoad] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isLoadingRef = useRef(false);

  // API fetch function
  const fetchSignatureSalutations = useCallback(async (pageNumber: number = 1, reset: boolean = false) => {
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
    
    if (!token) {
      showError('Authentication required. Please log in again.');
      return;
    }

    if (loading) return;

    try {
      setLoading(true);
      const response = await getSignatureSalutations(channelId, pageNumber, 10, token);
      
      if (response.success && response.data) {
        const newItems = response.data.items;
        
        if (reset) {
          setApiSignatureData(newItems);
        } else {
          // Prevent duplicates by filtering out items that already exist
          setApiSignatureData(prev => {
            const existingIds = new Set(prev.map(item => item.id));
            const uniqueNewItems = newItems.filter(item => !existingIds.has(item.id));
            return [...prev, ...uniqueNewItems];
          });
        }
        
        setPage(pageNumber);
        setTotalPages(response.data.pages);
        setTotalItems(response.data.total);
        setHasMore(pageNumber < response.data.pages);
      }
    } catch (error) {
      console.error('Error fetching signature salutations:', error);
      showError(error instanceof Error ? error.message : 'Failed to fetch signature salutations');
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
      if (!initialLoad) {
        setInitialLoad(true);
      }
    }
  }, [channelId, showError]);

  // Initial load
  useEffect(() => {
    if (showManagement && !initialLoad) {
      fetchSignatureSalutations(1, true);
    }
  }, [showManagement, fetchSignatureSalutations, initialLoad]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!tableContainerRef.current || loading || !hasMore || isLoadingRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      // Clear any existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Debounce the API call to prevent multiple rapid calls
      scrollTimeoutRef.current = setTimeout(() => {
        if (!isLoadingRef.current && !loading && hasMore) {
          isLoadingRef.current = true;
          fetchSignatureSalutations(page + 1, false);
        }
      }, 150); // 150ms debounce
    }
  }, [loading, hasMore, page, fetchSignatureSalutations]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container && showManagement) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll, showManagement]);

  // Reset loading ref when loading changes
  useEffect(() => {
    if (!loading) {
      isLoadingRef.current = false;
    }
  }, [loading]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Refresh data when refreshTrigger changes
  useEffect(() => {
    if (showManagement && refreshTrigger > 0) {
      fetchSignatureSalutations(1, true);
    }
  }, [refreshTrigger, showManagement, fetchSignatureSalutations]);

  const handleSignatureChange = (value: string) => {
    setSignature(value);
    onSignatureChange?.(value);
  };

  const handleSalutationChange = (value: string) => {
    setSalutation(value);
    onSalutationChange?.(value);
  };

  const handleEditSignature = () => {
    setSignatureModalOpen(true);
  };

  const handleSaveSignature = (newSignature: string) => {
    handleSignatureChange(newSignature);
  };

  const handleAddSignature = (data: SignatureAndSalutationData) => {
    // If in management mode, refresh the data from API with a small delay
    // to ensure the API has processed the new item
    if (showManagement) {
      setTimeout(() => {
        fetchSignatureSalutations(1, true);
      }, 500);
    } else {
      // Fallback for non-management mode
      const updated = [...localSignatureData, data];
      setLocalSignatureData(updated);
      onSignatureDataChange?.(updated);
    }
  };

  const handleDeleteSignatureClick = (id: string, name: string, index?: number) => {
    setDeleteConfirmDialog({
      open: true,
      index: index || -1,
      signatureName: name,
      id: id,
    });
  };

  const handleDeleteSignatureConfirm = async () => {
    const { id, index } = deleteConfirmDialog as any;
    
    // Close the confirmation dialog
    setDeleteConfirmDialog({
      open: false,
      index: -1,
      signatureName: '',
    });

    if (!id) return;

    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
    
    if (!token) {
      showError('Authentication required. Please log in again.');
      return;
    }

    setIsDeleting(id);
    
    try {
      const response = await deleteSignatureSalutation(id, token);
      
      if (response.success) {
        showSuccess('Signature and salutation deleted successfully!');
        
        if (showManagement) {
          // Remove from API data
          setApiSignatureData(prev => prev.filter(item => item.id !== id));
        } else {
          // Fallback for local data
          if (index !== undefined && index !== -1) {
            const updated = localSignatureData.filter((_, i) => i !== index);
            setLocalSignatureData(updated);
            onSignatureDataChange?.(updated);
          }
        }
      } else {
        showError(response.message || 'Failed to delete signature and salutation');
      }
    } catch (error) {
      console.error('Error deleting signature and salutation:', error);
      showError(error instanceof Error ? error.message : 'Failed to delete signature and salutation');
    } finally {
      setIsDeleting(null);
    }
  };

  const handleDeleteSignatureCancel = () => {
    setDeleteConfirmDialog({
      open: false,
      index: -1,
      signatureName: '',
    });
  };

  const handleEditSignatureClick = (signature: SignatureSalutationItem) => {
    setEditingSignature(signature);
    setEditModalOpen(true);
  };

  const handleEditSignatureClose = () => {
    setEditModalOpen(false);
    setEditingSignature(null);
  };

  const handleUpdateSignature = async (updatedData: SignatureAndSalutationData) => {
    if (!editingSignature) return;

    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
    
    if (!token) {
      showError('Authentication required. Please log in again.');
      return;
    }

    setIsUpdating(true);

    try {
      const updatePayload: UpdateSignatureSalutationRequest = {
        channel_id: updatedData.channel_id || editingSignature.channel_id,
        name: updatedData.name,
        signature: updatedData.signature,
        salutation: updatedData.salutation,
        is_default: updatedData.is_default,
      };

      const response = await updateSignatureSalutation(editingSignature.id, updatePayload, token);
      
      if (response.success) {
        showSuccess('Signature and salutation updated successfully!');
        
        // Update the item in the API data
        setApiSignatureData(prev => prev.map(item => 
          item.id === editingSignature.id 
            ? { ...item, ...updatePayload, updated_at: new Date().toISOString() }
            : item
        ));

        handleEditSignatureClose();
      } else {
        showError(response.message || 'Failed to update signature and salutation');
      }
    } catch (error) {
      console.error('Error updating signature and salutation:', error);
      showError(error instanceof Error ? error.message : 'Failed to update signature and salutation');
    } finally {
      setIsUpdating(false);
    }
  };

  // If showManagement is true, render the management interface
  if (showManagement) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            {/* <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Signature & Salutation
            </Typography>
            {initialLoad && apiSignatureData.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                Showing {apiSignatureData.length} of {totalItems} total items
                {hasMore && ' (scroll to load more)'}
              </Typography>
            )} */}
          </Box>
          {!hideAddButton && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setSignatureModalOpen(true)}
            >
              Add
            </Button>
          )}
        </Box>
        
        {!initialLoad ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: 200,
            border: '2px dashed #e0e0e0',
            borderRadius: 1,
            p: 3
          }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Loading signature salutations...
            </Typography>
          </Box>
        ) : apiSignatureData.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: 200,
            border: '2px dashed #e0e0e0',
            borderRadius: 1,
            p: 3
          }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              No signatures added yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click "Add" to create your first signature and salutation
            </Typography>
          </Box>
        ) : (
          <TableContainer 
            ref={tableContainerRef}
            sx={{ 
              maxHeight: 500, 
              overflowY: 'auto'
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    backgroundColor: 'rgba(25, 118, 210, 0.08)', 
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    color: 'rgb(28, 171, 222)',
                    borderBottom: '1px solid #e0e0e0',
                    py: 1.5,
                    px: 2
                  }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ 
                    backgroundColor: 'rgba(25, 118, 210, 0.08)', 
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    color: 'rgb(28, 171, 222)',
                    borderBottom: '1px solid #e0e0e0',
                    py: 1.5,
                    px: 2
                  }}>
                    Salutation
                  </TableCell>
                  <TableCell sx={{ 
                    backgroundColor: 'rgba(25, 118, 210, 0.08)', 
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    color: 'rgb(28, 171, 222)',
                    borderBottom: '1px solid #e0e0e0',
                    py: 1.5,
                    px: 2
                  }}>
                    Signature
                  </TableCell>
                  <TableCell sx={{ 
                    backgroundColor: 'rgba(25, 118, 210, 0.08)', 
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    color: 'rgb(28, 171, 222)',
                    borderBottom: '1px solid #e0e0e0',
                    py: 1.5,
                    px: 2
                  }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ 
                    backgroundColor: 'rgba(25, 118, 210, 0.08)', 
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    color: 'rgb(28, 171, 222)',
                    borderBottom: '1px solid #e0e0e0',
                    py: 1.5,
                    px: 2,
                    textAlign: 'center'
                  }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiSignatureData.map((item, index) => (
                  <TableRow 
                    key={item.id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                      '&:hover': {
                        backgroundColor: '#f8f9fa',
                      },
                      '&:last-child td': {
                        borderBottom: 0,
                      }
                    }}
                  >
                    <TableCell sx={{ 
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      color: '#333333',
                      py: 2.5,
                      borderBottom: '1px solid #e0e0e0',
                      backgroundColor: 'inherit'
                    }}>
                      {item.name}
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: '0.875rem',
                      color: '#666666',
                      py: 2.5,
                      borderBottom: '1px solid #e0e0e0',
                      backgroundColor: 'inherit'
                    }}>
                      <Box 
                        sx={{ 
                          maxWidth: 200, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {item.salutation || '-'}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: '0.875rem',
                      color: '#666666',
                      py: 2.5,
                      borderBottom: '1px solid #e0e0e0',
                      backgroundColor: 'inherit'
                    }}>
                      <Box 
                        sx={{ 
                          maxWidth: 200, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          '& p': { margin: 0, fontSize: '0.875rem' }
                        }}
                        dangerouslySetInnerHTML={{ 
                          __html: item.signature 
                            ? LinkifyText(item.signature, '', muiTheme)
                            : '-'
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: '0.875rem',
                      color: '#666666',
                      py: 2.5,
                      borderBottom: '1px solid #e0e0e0',
                      backgroundColor: 'inherit'
                    }}>
                      {item.is_default && (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 2,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            backgroundColor: '#e8f5e8',
                            color: '#2e7d32',
                            border: '1px solid #c8e6c9'
                          }}
                        >
                          Default
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ 
                      py: 2.5,
                      borderBottom: '1px solid #e0e0e0',
                      textAlign: 'center',
                      backgroundColor: 'inherit'
                    }}>
                      {isChannelAdmin && (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="Edit">
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleEditSignatureClick(item);
                              }}
                              disabled={isUpdating}
                              sx={{
                                color: 'rgb(28, 171, 222)',
                                '&:hover': {
                                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDeleteSignatureClick(item.id, item.name);
                              }}
                              disabled={isDeleting === item.id}
                              sx={{
                                color: '#d32f2f',
                                '&:hover': {
                                  backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                }
                              }}
                            >
                              {isDeleting === item.id ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20 }}>
                                  <Box
                                    sx={{
                                      width: 14,
                                      height: 14,
                                      border: '2px solid #f3f3f3',
                                      borderTop: '2px solid #d32f2f',
                                      borderRadius: '50%',
                                      animation: 'spin 1s linear infinite',
                                      '@keyframes spin': {
                                        '0%': { transform: 'rotate(0deg)' },
                                        '100%': { transform: 'rotate(360deg)' },
                                      },
                                    }}
                                  />
                                </Box>
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {loading && (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ 
                      textAlign: 'center', 
                      py: 3,
                      borderBottom: '1px solid #e0e0e0',
                      backgroundColor: 'white'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="#666666">
                          Loading more...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <AddSignatureAndSalutationModal
          open={signatureModalOpen}
          onClose={() => setSignatureModalOpen(false)}
          onSave={handleAddSignature}
          channelId={channelId}
        />

         {/* Delete Confirmation Dialog */}
         <Dialog
           open={deleteConfirmDialog.open}
           onClose={handleDeleteSignatureCancel}
           maxWidth="sm"
           fullWidth
           sx={{
             zIndex: 9999,
             '& .MuiDialog-paper': {
               backgroundColor: 'white',
             }
           }}
         >
           <DialogTitle>Confirm Delete</DialogTitle>
           <DialogContent>
             <Typography>
               Are you sure you want to delete the signature "{deleteConfirmDialog.signatureName}"? 
               This action cannot be undone.
             </Typography>
           </DialogContent>
           <DialogActions>
             <Button onClick={handleDeleteSignatureCancel}>
               Cancel
             </Button>
             <Button 
               onClick={handleDeleteSignatureConfirm} 
               color="error" 
               variant="contained"
             >
               Delete
             </Button>
           </DialogActions>
         </Dialog>

         {/* Edit Signature Modal */}
         {editingSignature && (
           <AddSignatureAndSalutationModal
             open={editModalOpen}
             onClose={handleEditSignatureClose}
             onSave={handleUpdateSignature}
             channelId={channelId}
             isEditing={true}
             initialData={{
               id: editingSignature.id,
               name: editingSignature.name,
               signature: editingSignature.signature,
               salutation: editingSignature.salutation,
               is_default: editingSignature.is_default,
               channel_id: editingSignature.channel_id,
             }}
           />
         )}
      </Box>
    );
  }

  // Original signature/salutation editor interface
  return (
    <Box sx={{ p: 2 }}>
      <Signature 
        value={signature} 
        onChange={handleSignatureChange} 
        onEditSignature={handleEditSignature}
      />
      <Salutation value={salutation} onChange={handleSalutationChange} />

      <AddSignatureModal
        open={signatureModalOpen}
        onClose={() => setSignatureModalOpen(false)}
        onSave={handleSaveSignature}
        initialSignature={signature}
      />
    </Box>
  );
};

export default SignatureAndSalutation;
